import { useState, useEffect } from 'react';
import { Flow, FlowEditData, NewFlowData } from '../types/flow.types';
import dataProvider from '../../services/api';

interface ErrorHandlerProps {
  showSuccessMessage: (message: string) => void;
  handleErrorWithLogout: (error: any) => void;
}

export const useFlowManagement = ({ showSuccessMessage, handleErrorWithLogout }: ErrorHandlerProps) => {
  const [processFlows, setProcessFlows] = useState<Flow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<number | null>(null);
  const [flowEditData, setFlowEditData] = useState<FlowEditData>({
    name: '',
    description: '',
    isActive: true,
    iconUrl: '',
    isSpecial: false,
  });
  const [newFlowData, setNewFlowData] = useState<NewFlowData>({ 
    name: '', 
    description: '', 
    isActive: true 
  });
  const [openCreateFlowModal, setOpenCreateFlowModal] = useState(false);

  const fetchProcessFlows = async () => {
    try {
      const response = await dataProvider.getProcessFlows();
      const flows = response.data;
      setProcessFlows(flows);
      if (flows.length > 0) {
        setSelectedFlow(flows[0].id);
        setFlowEditData({
          name: flows[0].name,
          description: flows[0].description,
          isActive: flows[0].isActive,
          iconUrl: flows[0].iconUrl || '',
          isSpecial: flows[0].isSpecial || false,
        });
      }
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleUploadIcon = async (file: File) => {
    if (!selectedFlow) return;
  
    try {
      await dataProvider.uploadIcon({ flowId: selectedFlow, file });
      const updatedFlow = await dataProvider.getProcessFlows();
      setProcessFlows(updatedFlow.data);
      const selectedFlowData = updatedFlow.data.find((flow: any) => flow.id === selectedFlow);
      if (selectedFlowData) {
        setFlowEditData({
          ...flowEditData,
          iconUrl: selectedFlowData.iconUrl || '',
        });
      }
      showSuccessMessage('Icon uploaded successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    } 
  };

  const handleCreateFlow = async () => {
    if (newFlowData.name.trim() && newFlowData.description.trim()) {
      try {
        await dataProvider.createFlow({
          name: newFlowData.name,
          description: newFlowData.description,
          isActive: true,
        });
        setOpenCreateFlowModal(false);
        setNewFlowData({ name: '', description: '', isActive: true });
        const response = await dataProvider.getProcessFlows();
        setProcessFlows(response.data);
        if (response.data.length > 0) {
          setSelectedFlow(response.data[response.data.length - 1].id);
        }
        showSuccessMessage('Flow created successfully.');
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    }
  };

  const handleDeleteFlow = async () => {
    if (!selectedFlow) return;
    try {
      await dataProvider.deleteFlow({ flowId: selectedFlow });
      const response = await dataProvider.getProcessFlows();
      setProcessFlows(response.data);
      if (response.data.length > 0) {
        setSelectedFlow(response.data[response.data.length - 1].id);
      }
      showSuccessMessage('Flow deleted successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  const handleUpdateFlow = async () => {
    if (!selectedFlow) return;
    try {
      const payload = {
        isActive: flowEditData.isActive,
        name: flowEditData.name,
        description: flowEditData.description,
        iconUrl: '',
        isSpecial: flowEditData.isSpecial,
      };
      await dataProvider.updateFlow({ id: selectedFlow, data: payload });
      const response = await dataProvider.getProcessFlows();
      setProcessFlows(response.data);
      showSuccessMessage('Flow updated successfully.');
    } catch (error: any) {
      handleErrorWithLogout(error);
    }
  };

  useEffect(() => {
    fetchProcessFlows();
  }, [dataProvider]);

  useEffect(() => {
    if (selectedFlow && processFlows.length > 0) {
      const selectedFlowData = processFlows.find((flow) => flow.id === selectedFlow);
      if (selectedFlowData) {
        setFlowEditData({
          name: selectedFlowData.name,
          description: selectedFlowData.description,
          isActive: selectedFlowData.isActive,
          iconUrl: selectedFlowData.iconUrl || '',
          isSpecial: selectedFlowData.isSpecial || false,
        });
      }
    }
  }, [selectedFlow, processFlows]);

  return {
    processFlows,
    selectedFlow,
    setSelectedFlow,
    flowEditData,
    setFlowEditData,
    newFlowData,
    setNewFlowData,
    openCreateFlowModal,
    setOpenCreateFlowModal,
    handleUploadIcon,
    handleCreateFlow,
    handleDeleteFlow,
    handleUpdateFlow,
  };
};